import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useI18n } from '../i18n/i18n';
import { getExerciseDetails } from '../services/apiClient';
import { getExerciseGuide, isCuratedExercise } from '../data/exerciseGuides';
import { getExercisesByMuscleGroup } from '../services/exerciseService';
import SeoHead from '../components/SeoHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { buildLocalizedAlternates } from '../seo/routePolicy';
import './ExerciseDetails.css';

const muscleImageMap = {
    trapezius: 'Trapezius',
    deltoids: 'Deltoids',
    rear_deltoids: 'Rear_Deltoids',
    chest: 'Chest',
    upper_back: 'Upper_Back',
    back: 'Back',
    lower_back: 'Lower_Back',
    biceps: 'Biceps',
    triceps: 'Triceps',
    forearms: 'Forearms',
    abs: 'Abs',
    obliques: 'Obliques',
    hip_flexors: 'Hip_Flexor',
    glutes: 'Glutes',
    legs: 'Legs',
    quadriceps: 'Quadriceps',
    hamstrings: 'Hamstrings',
    calves: 'Calves'
};

function ExerciseDetails() {
    const { exerciseId } = useParams();
    const { getToken } = useAuth();
    const { t, language, localizePath } = useI18n();
    
    // Always initialize synchronously from rich local exercise guide so no blank screen / thin content occurs
    const initialGuide = useMemo(() => {
        return getExerciseGuide(exerciseId, language);
    }, [exerciseId, language]);

    const [details, setDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('guide');

    useEffect(() => {
        let mounted = true;

        async function fetchDetails() {
            try {
                const data = await getExerciseDetails(getToken, exerciseId, language);
                if (mounted && data) {
                    setDetails(data);
                }
            } catch (err) {
                // Silently fallback to built-in comprehensive guide
            }
        }

        fetchDetails();

        return () => {
            mounted = false;
        };
    }, [exerciseId, language, getToken]);

    const guide = initialGuide;
    const primaryMuscle = guide.primaryMuscle;
    const muscleImageName = primaryMuscle ? muscleImageMap[primaryMuscle] : null;
    const displayTitle = guide.name;

    // Get 4 related exercises in the same category for interlinking
    const relatedExercises = useMemo(() => {
        if (!primaryMuscle) return [];
        const groupExercises = getExercisesByMuscleGroup(primaryMuscle, "primary") || [];
        return groupExercises
            .filter(e => e.id !== exerciseId)
            .slice(0, 4);
    }, [primaryMuscle, exerciseId]);

    const exerciseJsonLd = useMemo(() => {
        const siteUrl = "https://fit-log.com";
        const isUk = language.startsWith('uk');
        return {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": t("breadcrumbs.dashboard"), "item": `${siteUrl}${localizePath("/")}` },
                        { "@type": "ListItem", "position": 2, "name": t("breadcrumbs.selectExercise"), "item": `${siteUrl}${localizePath("/SelectExercise")}` },
                        ...(primaryMuscle ? [{
                            "@type": "ListItem",
                            "position": 3,
                            "name": t(`muscleGroups.${primaryMuscle}`) || primaryMuscle,
                            "item": `${siteUrl}${localizePath(`/SelectExercise/${primaryMuscle}`)}`
                        }] : []),
                        { "@type": "ListItem", "position": primaryMuscle ? 4 : 3, "name": displayTitle, "item": `${siteUrl}${localizePath(`/exercises/${exerciseId}`)}` }
                    ]
                },
                {
                    "@type": "ExerciseAction",
                    "name": displayTitle,
                    "description": guide.instructions?.join(" ") || displayTitle,
                    "exerciseType": guide.equipment || "Strength",
                    "url": `${siteUrl}${localizePath(`/exercises/${exerciseId}`)}`
                }
            ]
        };
    }, [primaryMuscle, displayTitle, exerciseId, guide, t, localizePath, language]);

    const seoTitle = language.startsWith('uk')
        ? `${displayTitle}: техніка виконання та типові помилки | Fit-Log`
        : `${displayTitle}: Technique Guide & Form Mistakes | Fit-Log`;

    const seoDescription = language.startsWith('uk')
        ? `Дізнайтеся правильну техніку виконання вправи ${displayTitle}. Покрокова інструкція, цільові м'язи, типові помилки та рекомендації щодо підходів і повторень.`
        : `Master the ${displayTitle} with proper form. Step-by-step instructions, target muscles, common mistakes to avoid, and recommended sets and reps.`;

    return (
        <div className="exercise-details-page">
            <SeoHead
                title={seoTitle}
                description={seoDescription}
                canonicalPath={localizePath(`/exercises/${exerciseId}`)}
                alternateLocales={buildLocalizedAlternates(`/exercises/${exerciseId}`)}
                jsonLd={exerciseJsonLd}
                noIndex={!isCuratedExercise(exerciseId)}
            />
            
            <Breadcrumbs />
            
            <div className="exercise-details-header">
                <h1>{displayTitle}</h1>
                <div className="exercise-meta-row">
                    <span className="meta-pill equipment-pill">
                        <strong>{t("exerciseDetails.equipment")}</strong> {guide.equipment}
                    </span>
                    {primaryMuscle && (
                        <Link 
                            to={localizePath(`/SelectExercise/${primaryMuscle}`)} 
                            className="meta-pill muscle-pill"
                        >
                            {t(`muscleGroups.${primaryMuscle}`) || guide.primaryMuscleName}
                        </Link>
                    )}
                </div>
            </div>

            <div className="tabs-container">
                <button 
                    className={`tab-button ${activeTab === 'guide' ? 'active' : ''}`}
                    onClick={() => setActiveTab('guide')}
                >
                    {t("exerciseDetails.tabGuide")}
                </button>
                <button 
                    className={`tab-button ${activeTab === 'muscles' ? 'active' : ''}`}
                    onClick={() => setActiveTab('muscles')}
                >
                    {t("exerciseDetails.tabMuscles")}
                </button>
            </div>

            <div className="tab-content">
                {activeTab === 'guide' && (
                    <div className="guide-content">
                        {(details?.diagram_url || guide.diagramUrl) && (
                            <img 
                                src={details?.diagram_url || guide.diagramUrl} 
                                alt={`${displayTitle} diagram`} 
                                className="api-diagram" 
                            />
                        )}

                        {guide.instructions && guide.instructions.length > 0 && (
                            <section className="guide-section">
                                <h2>{t("exerciseDetails.instructions")}</h2>
                                <ol className="instructions-list">
                                    {guide.instructions.map((step, idx) => (
                                        <li key={idx}>{step}</li>
                                    ))}
                                </ol>
                            </section>
                        )}

                        {guide.tips && (
                            <div className="guide-card tips-card">
                                <h3>💡 {language.startsWith('uk') ? 'Порада щодо техніки' : 'Pro Tip & Form Cue'}</h3>
                                <p>{guide.tips}</p>
                            </div>
                        )}

                        {guide.commonMistakes && guide.commonMistakes.length > 0 && (
                            <section className="guide-section">
                                <h2>{t("exerciseDetails.commonMistakes")}</h2>
                                <ul className="mistakes-list">
                                    {guide.commonMistakes.map((mistake, idx) => (
                                        <li key={idx}>{mistake}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {guide.volume && (
                            <div className="guide-card volume-card">
                                <h3>📊 {language.startsWith('uk') ? 'Рекомендовані підходи та повторення' : 'Recommended Sets & Reps'}</h3>
                                <p>{guide.volume}</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'muscles' && (
                    <div className="muscles-content">
                        {muscleImageName ? (
                            <>
                                <img 
                                    src={`/images/muscles/${muscleImageName}.webp`} 
                                    alt={`Target muscles: ${primaryMuscle}`} 
                                    className="anatomy-image" 
                                />
                                <div className="muscle-anatomy-info">
                                    <h3>{t(`muscleGroups.${primaryMuscle}`) || guide.primaryMuscleName}</h3>
                                    <p>
                                        {language.startsWith('uk')
                                            ? `Ця вправа переважно навантажує ${guide.primaryMuscleName.toLowerCase()}. Використовуйте повну амплітуду руху для стимуляції росту та сили.`
                                            : `This exercise primarily recruits the ${guide.primaryMuscleName.toLowerCase()}. Use a full range of motion to maximize hypertrophy and strength.`}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <p>No anatomy image available for this exercise.</p>
                        )}
                    </div>
                )}
            </div>

            {relatedExercises.length > 0 && (
                <div className="related-exercises-block">
                    <h3>{language.startsWith('uk') ? 'Схожі вправи цієї групи' : 'Related Exercises in this Group'}</h3>
                    <div className="related-exercise-pills">
                        {relatedExercises.map(ex => (
                            <Link 
                                key={ex.id} 
                                to={localizePath(`/exercises/${ex.id}`)}
                                className="related-pill"
                            >
                                {ex.name}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="back-button-container" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap", marginTop: "24px" }}>
                {primaryMuscle && (
                    <Link to={localizePath(`/SelectExercise/${primaryMuscle}`)} className="appButton secondary-button">
                        ← {t(`muscleGroups.${primaryMuscle}`) || primaryMuscle}
                    </Link>
                )}
                <Link to={localizePath(`/LogExercise/${exerciseId}`)} className="appButton back-button">
                    {language.startsWith('uk') ? 'Записати тренування' : 'Log This Exercise'}
                </Link>
            </div>
        </div>
    );
}

export default ExerciseDetails;
